<template>
  <div
    class="newsfeed-item"
    :class="{
      'newsfeed-item--active': isCurrentCardActive,
      'newsfeed-item--chosen': chosenCardList.includes(props.data.id),
    }"
    @click.stop="setCurrentNews"
  >
    <div
      class="newsfeed-item__header"
      :class="{ 'newsfeed-item__header-selector--active': isToolbarActive }"
    >
      <!--    Чек-бокс для отметки новостей при включенном тулбаре    -->
      <div v-if="isToolbarActive" class="newsfeed-item__header-selector">
        <Checkbox
          v-model="proxyChosenCardList"
          :value="props.data.id"
          :disabled="isSelectionDisabled"
        />
      </div>
      <!--    Заголовок    -->
      <div class="newsfeed-item__header-title" v-html="props.data.title" />
      <!--    Кнопка добавления в избранное    -->
      <button
        v-if="
          rubricsStore.chosenRubrics.length >= 0 &&
          rubricsStore.isCurrentRubricFixed()
        "
        class="newsfeed-item__button"
        :class="favoriteButtonClasses"
        @click="addToFavorite"
      >
        <SaveIcon
          class="newsfeed-item__button-icon"
          :class="favoriteIconClasses"
        />
      </button>
    </div>
    <div class="newsfeed-item__body" v-html="props.data.text" />
    <div class="newsfeed-item__footer">
      <div
        v-if="
          rubricsStore.chosenRubrics.length >= 0 &&
          rubricsStore.isCurrentRubricFixed()
        "
        class="newsfeed-item__footer-meta"
      >
        <!--    Сантимент    -->
        <Component
          :is="
            props.data.sentiment > 0
              ? GoodIcon
              : props.data.sentiment < 0
              ? SadIcon
              : NeutralIcon
          "
          v-if="props.data.sentiment !== null"
          class="newsfeed-item__button-sentiment"
          :active="isCurrentCardActive"
          :news="true"
        />
        <!--    Значимость    -->
        <Chip
          v-if="props.data.importance && props.data.importance > 0"
          :model-value="true"
          :use-cross-icon="false"
          :use-hover="false"
          :color="getImportanceName(props.data.importance)"
        >
          {{
            t(
              "news_page_body_news_feed_item.importance." +
                getImportanceLabel(props.data.importance)
            )
          }}
        </Chip>
        <Chip
          v-if="props.data.cities?.length"
          :use-cross-icon="false"
          max-width="180px"
          :use-hover="false"
          :color="isCurrentCardActive ? 'gray' : ''"
          type="text"
          :use-location-icon="true"
        >
          {{ props.data.cities[0].city }}
        </Chip>
      </div>
      <div class="newsfeed-item__footer-date">
        {{ convertTimestampToData(props.data.date) }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { convertTimestampToData } from "@/tools/date-time-tools.js";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import GoodIcon from "@/components/Icons/GoodIcon.vue";
import SadIcon from "@/components/Icons/SadIcon.vue";
import NeutralIcon from "@/components/Icons/NeutralIcon.vue";
import { t } from "@/controllerLK/GlobusLocalization";
import SaveIcon from "@/components/Icons/SaveIcon.vue";

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  boxWidth: {
    type: Number,
    required: true,
  },
  isToolbarActive: {
    type: Boolean,
    default: false,
  },
  chosenCardList: {
    type: Array,
    required: true,
    default: () => [],
  },
  isSelectionDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:chosen-card-list"]);

// Прокси-computed для v-model на отмеченные карточки
const proxyChosenCardList = computed({
  get() {
    return props.chosenCardList;
  },
  set(newList) {
    emit("update:chosen-card-list", newList);
  },
});

const newsFeedStore = useNewsFeedStore();
const rubricsStore = useRubricsStore();

const addToFavorite = () => {
  newsFeedStore.items[props.index].is_favourite =
    !newsFeedStore.items[props.index].is_favourite;
  newsFeedStore.setFavorite(
    props.data.id,
    newsFeedStore.items[props.index].is_favourite
  );
};

function getRubricCard() {
  if (newsFeedStore.currentNews.rubric_ids) {
    const rubricIdsSet = new Set(newsFeedStore.currentNews.rubric_ids);
    rubricsStore.currentCardRubrics = rubricsStore.allRubrics.filter((rubric) =>
      rubricIdsSet.has(rubric.id)
    );
  }
}

let timeout;

function debounce(func, delay) {
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
}

const debouncedGetRubricCard = debounce(getRubricCard, 100);

// Установить активную новость.
const setCurrentNews = async () => {
  if (
    !newsFeedStore.downloadOn &&
    newsFeedStore.currentNews.id !== props.data.id
  ) {
    rubricsStore.currentCardRubrics = [];
    newsFeedStore.currentNews = props.data;
    newsFeedStore.currentNews.sentiment = normalizeSentiment(
      newsFeedStore.currentNews.sentiment
    );

    debouncedGetRubricCard();
  } else if (newsFeedStore.downloadOn) {
    if (newsFeedStore.downloadList.find((item) => item === props.data.id)) {
      newsFeedStore.downloadList = newsFeedStore.downloadList.filter(
        (item) => item !== props.data.id
      );
    } else {
      newsFeedStore.downloadList.push(props.data.id);
    }
  }
};

// Определение сантимента для установки активной новости
const normalizeSentiment = (value) => {
  if (value < 0) return -1;
  if (value > 0) return 1;
  return 0;
};

// Является ли текущая новостная карточка активной
const isCurrentCardActive = computed(
  () =>
    newsFeedStore.currentNews?.id === props.data.id &&
    !!newsFeedStore.currentNews?.id
);

// Упрощенная логика для состояний кнопки избранного
const favoriteButtonClasses = computed(() => ({
  "newsfeed-item__button--favorite": props.data.is_favourite,
  "newsfeed-item__button--active-card": isCurrentCardActive.value,
}));

const favoriteIconClasses = computed(() => ({
  "newsfeed-item__button-icon--favorite": props.data.is_favourite,
  "newsfeed-item__button-icon--active-card": isCurrentCardActive.value,
}));

// Получение наименования значимости
const getImportanceName = (importance) => {
  if (importance === 1) return "green";
  if (importance === 2) return "yellow";
  if (importance === 3) return "orange";
  if (importance === 4) return "red";
  return "gray";
};

// Получение лейбла значимости
const getImportanceLabel = (importance) => {
  if (importance === 1) return "not_critical";
  if (importance === 2) return "moderately";
  if (importance === 3) return "important";
  if (importance === 4) return "critical";
  return "unset";
};
</script>

<style scoped lang="scss">
.newsfeed-item {
  height: 205px; // По макету высота 195px, но в таком случае не помещается 3 строки текста. Уточнить у дизайнеров.
  min-height: 202px; // Задача 2026
  box-shadow: 0 4px 8px 0 #00000024;
  background: #ffffff;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  gap: 20px;
  flex-direction: column;
  cursor: default;

  &__button {
    width: 36px;
    height: 36px;
    border-radius: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--fix-button-secondary-inactive-default);

    &:hover {
      background-color: var(--fix-button-secondary-inactive-hover);
    }

    &--favorite {
      background-color: var(--fix-button-secondary-active-default);

      &:hover {
        background-color: var(--fix-button-secondary-active-hover);
      }
    }

    &--active-card {
      // Стили для активной карточки
      background-color: var(--fix-button-dark-inactive-default);

      &:hover {
        background-color: var(--fix-button-dark-inactive-hover);
      }
    }

    // Комбинированное состояние: активная карточка + избранное
    &.newsfeed-item__button--favorite.newsfeed-item__button--active-card {
      background-color: var(--fix-button-dark-active-default);

      &:hover {
        background-color: var(--fix-button-dark-active-hover);
      }
    }

    &-icon {
      min-width: 17px;
      min-height: 17px;
      color: var(--icon-icon-primary);

      &--favorite {
        fill: var(--icon-icon-primary);
        color: var(--icon-icon-primary);
      }

      &--active-card {
        // Стили для активной карточки
        color: var(--fix-button-dark-inactive-icon);
      }

      // Комбинированное состояние: активная карточка + избранное
      &.newsfeed-item__button-icon--favorite.newsfeed-item__button-icon--active-card {
        fill: var(--fix-button-dark-inactive-icon);
        color: var(--fix-button-dark-inactive-icon);
      }
    }

    &-sentiment {
      width: 20px;
      height: 20px;
    }
  }

  &:hover {
    background: var(--background-bg-active-hover);
  }

  &__body {
    font-size: 14px;
    line-height: 18px; // (3 строки текста) В макете разная информация, где то 14, где то 18.
    font-weight: 400;
    color: #373d3f;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    text-overflow: ellipsis;
    min-height: 54px; // Задача 2026
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-meta {
      display: flex;
      gap: 6px;
      align-items: center;
    }

    &-date {
      color: #898989;
      font-size: 12px;
      font-weight: 400;
      margin-left: auto;
    }
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr 36px;
    gap: 10px;
    align-items: center;
    min-height: 44px; // Задача 2026

    &-selector {
      width: 20px;
      height: 20px;

      &--active {
        grid-template-columns: 20px 1fr 36px;
      }

      input {
        display: none;
      }

      label {
        cursor: pointer;
      }

      :deep(
          .checkbox__box:has(.checkbox__input:disabled)
            .checkbox__icon
            path:first-child
        ) {
        fill: var(--icon-icon-disabled);
      }
    }

    &-title {
      color: #373d3f;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }
  }

  // Стили для отмеченных карточек
  &--chosen {
    outline-offset: -1.5px;
    outline: 1.5px solid #1f9aff;
  }

  // Стили для активной карточки.
  &--active {
    background: #7b87a5;
    color: var(--text-text-on-inverse);

    &:hover {
      background: #7b87a5;
      color: var(--text-text-on-inverse);
    }

    .newsfeed-item__header-title {
      color: var(--text-text-on-inverse);
    }

    .newsfeed-item__body {
      color: var(--text-text-on-inverse);
    }

    .newsfeed-item__footer {
      &-date {
        color: var(--text-text-on-inverse);
      }
    }
  }
}
</style>
